<template>
  <div class="domain-checker">
    <div class="checker-container">
      <h2 class="page-title">Domain Checker</h2>
      <p class="page-description">Enter a URL or email address to check if website or sender is known and trusted</p>
      
      <div class="input-section">
        <div class="input-group">
          <label for="url-input" class="input-label">URL or Email:</label>
          <input
            id="url-input"
            v-model="inputUrl"
            type="text"
            placeholder="https://example.com, http://subdomain.example.com, or email like name@domain.com"
            class="url-input"
            @keyup.enter="checkDomain"
          />
          <button @click="checkDomain" class="check-button" :disabled="!inputUrl.trim()">
            Check Domain
          </button>
        </div>
      </div>

      <div v-if="result" class="result-section">
        <div :class="['result-card', result.found ? 'found' : 'not-found']">
          <div class="result-header">
            <h3>{{ result.found ? 'Domain Found!' : 'Domain Not Found' }}</h3>
            <span class="status-badge" :class="result.found ? 'found' : 'not-found'">
              {{ result.found ? 'TRUSTED' : 'NOT KNOWN' }}
            </span>
          </div>
          
          <div class="result-details">
            <div class="detail-row">
              <strong>Input {{ result.inputType === 'email' ? 'Email' : 'URL' }}:</strong> {{ result.inputUrl }}
            </div>
            <div class="detail-row">
              <strong>Extracted Domain:</strong> {{ result.extractedDomain }}
            </div>
            
            <div v-if="result.found" class="match-details">
              <div class="detail-row">
                <strong>Description:</strong> {{ result.domainInfo.description }}
              </div>
              <div class="detail-row">
                <strong>Owner:</strong> {{ result.domainInfo.owner }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="error" class="error-section">
        <div class="error-card">
          <h3>Error</h3>
          <p>{{ error }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DomainChecker',
  data() {
    return {
      inputUrl: '',
      result: null,
      error: null,
      domainList: [],
      lastKnownUpdate: null
    };
  },
  async mounted() {
    await this.loadDomainList();
    
    // Listen for domain updates from other components
    this.checkForUpdates();
  },
  methods: {
    async loadDomainList() {
      try {
        // Add cache-busting parameter to ensure fresh data
        const timestamp = new Date().getTime();
        const response = await fetch(`/api/domains?t=${timestamp}`, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        const data = await response.json();
        
        if (data.success) {
          this.domainList = data.domains;
        } else {
          throw new Error(data.error || 'Failed to load domains');
        }
      } catch (error) {
        console.error('Error loading domain list:', error);
        this.error = 'Failed to load domain list';
      }
    },
    
    checkForUpdates() {
      // Check for updates every 2 seconds
      setInterval(async () => {
        const lastUpdate = localStorage.getItem('domainsUpdated');
        if (lastUpdate && lastUpdate !== this.lastKnownUpdate) {
          console.log('Domain list updated, refreshing...');
          await this.loadDomainList();
          this.lastKnownUpdate = lastUpdate;
        }
      }, 2000);
    },
    
    parseDomainList(text) {
      const lines = text.trim().split('\n');
      return lines.map(line => {
        const parts = line.trim().split(/\s+/);
        if (parts.length >= 3) {
          return {
            domain: parts[0],
            description: parts.slice(1, -1).join(' '),
            owner: parts[parts.length - 1]
          };
        }
        return null;
      }).filter(item => item !== null);
    },
    
    isEmail(input) {
      // Basic email validation regex
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(input.trim());
    },
    
    extractDomainFromEmail(email) {
      try {
        const parts = email.split('@');
        if (parts.length !== 2) {
          throw new Error('Invalid email format');
        }
        return parts[1].toLowerCase();
      } catch (error) {
        throw new Error('Invalid email format');
      }
    },
    
    extractDomain(url) {
      try {
        // Check if input is an email address
        if (this.isEmail(url)) {
          return this.extractDomainFromEmail(url);
        }
        
        // Remove protocol if present
        let domain = url.replace(/^https?:\/\//, '');
        
        // Remove www. prefix
        domain = domain.replace(/^www\./, '');
        
        // Remove path, query parameters, and fragments
        domain = domain.split('/')[0];
        domain = domain.split('?')[0];
        domain = domain.split('#')[0];
        
        // Remove port if present
        domain = domain.split(':')[0];
        
        return domain.toLowerCase();
      } catch (error) {
        throw new Error('Invalid URL or email format');
      }
    },
    
    checkDomain() {
      if (!this.inputUrl.trim()) {
        this.error = 'Please enter a URL or email address';
        return;
      }
      
      this.error = null;
      this.result = null;
      
      try {
        const extractedDomain = this.extractDomain(this.inputUrl);
        const isEmailInput = this.isEmail(this.inputUrl);
        
        // Check for exact match or subdomain match
        const match = this.domainList.find(domainInfo => {
          const domain = domainInfo.domain.toLowerCase();
          return extractedDomain === domain || extractedDomain.endsWith('.' + domain);
        });
        
        this.result = {
          inputUrl: this.inputUrl,
          extractedDomain: extractedDomain,
          found: !!match,
          domainInfo: match || null,
          inputType: isEmailInput ? 'email' : 'url'
        };
        
      } catch (error) {
        this.error = error.message;
      }
    }
  }
};
</script>

<style scoped>
.domain-checker {
  max-width: 800px;
  margin: 0 auto;
}

.checker-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 2rem;
}

.page-title {
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-size: 2rem;
}

.page-description {
  color: #7f8c8d;
  margin-bottom: 2rem;
  font-size: 1.1rem;
}

.input-section {
  margin-bottom: 2rem;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.input-label {
  font-weight: 600;
  color: #2c3e50;
}

.url-input {
  padding: 0.75rem;
  border: 2px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s;
}

.url-input:focus {
  outline: none;
  border-color: #3498db;
}

.check-button {
  background: #3498db;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s;
  align-self: flex-start;
}

.check-button:hover:not(:disabled) {
  background: #2980b9;
}

.check-button:disabled {
  background: #bdc3c7;
  cursor: not-allowed;
}

.result-section {
  margin-top: 2rem;
}

.result-card {
  border-radius: 8px;
  padding: 1.5rem;
  border-left: 4px solid;
}

.result-card.found {
  background: #d5f4e6;
  border-left-color: #27ae60;
}

.result-card.not-found {
  background: #fdf2e9;
  border-left-color: #e67e22;
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.result-header h3 {
  margin: 0;
  color: #2c3e50;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.found {
  background: #27ae60;
  color: white;
}

.status-badge.not-found {
  background: #e67e22;
  color: white;
}

.result-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.detail-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.detail-row strong {
  color: #2c3e50;
  min-width: 120px;
}

.match-details {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(0,0,0,0.1);
}

.error-section {
  margin-top: 2rem;
}

.error-card {
  background: #fdf2f2;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
  padding: 1.5rem;
  color: #721c24;
}

.error-card h3 {
  margin: 0 0 0.5rem 0;
  color: #721c24;
}

@media (max-width: 768px) {
  .checker-container {
    padding: 1rem;
  }
  
  .result-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .detail-row {
    flex-direction: column;
  }
  
  .detail-row strong {
    min-width: auto;
  }
}
</style>
